import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Box,
    TextField, MenuItem, Button, Slide, Typography, Chip, FormControlLabel, Switch,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Icon } from '@iconify/react/dist/iconify.js';
import { ROLE_STATUS_OPTIONS } from '../../ManageEmployee/Component/employeeConstants';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// Extract the menus array from the API's `access` object (already parsed).
const menusFromAccess = (access) => {
    if (!access) return [];
    if (Array.isArray(access.menus)) return access.menus;
    if (Array.isArray(access)) return access;
    return [];
};

const EmployeeTypeFormModal = ({ open, mode = 'add', role, submitting = false, onClose, onSubmit }) => {
    const isEdit = mode === 'edit';
    const [type, setType] = useState('');
    const [status, setStatus] = useState('A');
    const [switchToAdmin, setSwitchToAdmin] = useState(false);
    const [menus, setMenus] = useState([]);
    const [menuInput, setMenuInput] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        if (isEdit && role) {
            setType(role.type ?? '');
            setStatus(role.status === 'I' ? 'I' : 'A');
            setSwitchToAdmin(role.switch_to_admin === 1 || role.switch_to_admin === true);
            setMenus(menusFromAccess(role.access));
        } else {
            setType(''); setStatus('A'); setSwitchToAdmin(false); setMenus([]);
        }
        setMenuInput('');
        setError('');
    }, [open, isEdit, role]);

    const addMenu = () => {
        const v = menuInput.trim();
        if (!v) return;
        if (!menus.includes(v)) setMenus((p) => [...p, v]);
        setMenuInput('');
    };
    const removeMenu = (m) => setMenus((p) => p.filter((x) => x !== m));

    const handleSubmit = () => {
        if (!type.trim()) { setError('Type name is required'); return; }
        const payload = {
            type: type.trim(),
            access: { menus },
            switch_to_admin: switchToAdmin,
            status,
        };
        if (isEdit) payload.id = role.employee_type_id ?? role.id;
        onSubmit(payload, { isEdit });
    };

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} TransitionComponent={Transition}
            maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <Box sx={{ background: 'linear-gradient(135deg, #487fff 0%, #6366f1 100%)', color: 'white' }}>
                <DialogTitle sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Icon icon={isEdit ? 'lucide:edit' : 'mdi:badge-account-horizontal-outline'} width="26" height="26" />
                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
                                {isEdit ? 'Edit Employee Type' : 'Add Employee Type'}
                            </Typography>
                        </Box>
                        <IconButton onClick={onClose} disabled={submitting} size="small"
                            sx={{ color: 'white', backgroundColor: 'rgba(255,255,255,0.15)' }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>
            </Box>

            <DialogContent sx={{ p: 2.5, bgcolor: '#f8fafc' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField label="Type Name" size="small" fullWidth required
                        value={type} onChange={(e) => { setType(e.target.value); setError(''); }}
                        error={!!error} helperText={error} placeholder="e.g. Night Guard" />

                    <TextField select label="Status" size="small" fullWidth
                        value={status} onChange={(e) => setStatus(e.target.value)}>
                        {ROLE_STATUS_OPTIONS.map((o) => (<MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>))}
                    </TextField>

                    <FormControlLabel
                        control={<Switch checked={switchToAdmin} onChange={(e) => setSwitchToAdmin(e.target.checked)} />}
                        label={
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Switch to Admin</Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    Employees of this type receive the SOCIETY_ADMIN role.
                                </Typography>
                            </Box>
                        }
                    />

                    <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>
                            Accessible Menus
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField size="small" fullWidth placeholder="Add a menu key (e.g. invoice)"
                                value={menuInput} onChange={(e) => setMenuInput(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addMenu(); } }} />
                            <Button variant="outlined" onClick={addMenu} sx={{ whiteSpace: 'nowrap' }}>Add</Button>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
                            {menus.length === 0 ? (
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>No menus added</Typography>
                            ) : (
                                menus.map((m) => (
                                    <Chip key={m} label={m} size="small" onDelete={() => removeMenu(m)}
                                        sx={{ bgcolor: '#eef2ff', color: '#4338ca' }} />
                                ))
                            )}
                        </Box>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, gap: 1, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                <Button onClick={onClose} disabled={submitting} variant="outlined"
                    sx={{ borderColor: 'rgba(0,0,0,0.23)', color: 'text.primary' }}>Cancel</Button>
                <Button onClick={handleSubmit} disabled={submitting} variant="contained"
                    sx={{ backgroundColor: '#487fff', '&:hover': { backgroundColor: '#3f6ede' } }}>
                    {submitting ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Type')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EmployeeTypeFormModal;
